define(['./namespace.js', './extend', './formatting', './util'], function(namespace, extend, formatting, util) {
    'use strict';

    var defaultConfiguration = null,
        applicationConfiguration = null,
        baseLabels = null,
        baseSettings = null,
        options,
        _componentInstanceConfigCache = {},
        componentInstanceSettingsCache = {},
        componentInstanceLabelsCache = {},
        componentInstanceLanguageLabelsCache = {},
        componentInstanceTypeCache = {},
        modulePaths = {},
        _defaultConfigFragmentCache = {},
        _defaultSettingsFragmentCache = {},
        _instanceSettingsFragmentCache = {},
        _defaultLabelsFragmentCache = {},
        _instanceLabelsFragmentCache = {},
        _typeConfigs = {};

    var defaultBaseLabels = require('../config/default-base-labels.json');
    var defaultBaseSettings = require('../config/default-base-settings.json');

    function getNonUnderscored(obj) {
        var newObj = {};
        util.each(obj, function(v, k) {
            if (k.charAt(0) !== '_') {
                newObj[k] = v;
            }
        });

        return newObj;
    }

    function getUnderscored(obj) {
        var newObj = {};
        util.each(obj, function(v, k) {
            if (k.charAt(0) === '_') {
                newObj[k] = v;
            }
        });

        return newObj;
    }

    function mergeCustomizer(a, b, key) {
        if (util.isArray(b)) {
            return b;
        }
    }

    function mergeCustomizerExceptUnderscore(a, b, key) {
        if (key && util.isString(key) && key.charAt(0) === '_') {
            return a;
        }

        if (util.isArray(b)) {
            return b;
        }
    }

    function _applyEnv(obj, path) {
        path = path || '';
        if (path.indexOf('settings') >= 0 && obj.__env) {
            var env = obj.__env[options.env];
            if (env) {
                util.merge(obj, env);
            }
        }

        util.each(obj, function(v, k) {
            if (util.isPlainObject(v) && ['labels'].indexOf(k) < 0) {
                obj[k] = _applyEnv(v, path ? path + '.' + k : k);
            }
        });

        return obj;
    }

    function defaultConfigCacher(path, type, currentDefaultConfig) {
        if (_defaultConfigFragmentCache[path]) {
            return _defaultConfigFragmentCache[path];
        }

        var typeExtended = defaultConfiguration[type] || {};
        return _defaultConfigFragmentCache[path] = util.merge({}, typeExtended, currentDefaultConfig, mergeCustomizer);
    }

    function defaultSettingsCacher(path, currentDefaultSettings, currentDefaultConfig) {
        if (_defaultSettingsFragmentCache[path]) {
            return _defaultSettingsFragmentCache[path];
        }

        return _defaultSettingsFragmentCache[path] = util.merge({}, getNonUnderscored(currentDefaultSettings), currentDefaultConfig &&
            currentDefaultConfig.settings ? currentDefaultConfig.settings : {}, mergeCustomizer);
    }

    function instanceSettingsCacher(path, currentInstance, currentSettings, currentDefaultSettings) {
        if (_instanceSettingsFragmentCache[path]) {
            return _instanceSettingsFragmentCache[path];
        }

        return _instanceSettingsFragmentCache[path] = util.merge({}, util.merge({}, getUnderscored(currentDefaultSettings), getUnderscored(
            currentInstance.settings), mergeCustomizer), getNonUnderscored(currentSettings), currentInstance.settings, mergeCustomizerExceptUnderscore);
    }

    function defaultLabelsCacher(path, currentDefaultLabels, currentDefaultConfig) {
        if (_defaultLabelsFragmentCache[path]) {
            return _defaultLabelsFragmentCache[path];
        }

        return _defaultLabelsFragmentCache[path] = util.merge({}, getNonUnderscored(currentDefaultLabels), currentDefaultConfig && currentDefaultConfig.labels ?
            currentDefaultConfig.labels : {}, mergeCustomizer);
    }

    function instanceLabelsCacher(path, currentInstance, currentLabels, currentDefaultLabels) {
        if (_instanceLabelsFragmentCache[path]) {
            return _instanceLabelsFragmentCache[path];
        }

        return _instanceLabelsFragmentCache[path] = util.merge({}, util.merge({}, getUnderscored(currentDefaultLabels), getUnderscored(
            currentInstance.labels), mergeCustomizer), getNonUnderscored(currentLabels), currentInstance.labels, mergeCustomizerExceptUnderscore);
    }

    function storeTypeConfigs(typeConfigs) {
        util.each(typeConfigs, function(typeConfig, type) {
            if (_typeConfigs[type]) {
                _typeConfigs[type] = util.merge({}, _typeConfigs[type], typeConfig);
            } else {
                _typeConfigs[type] = typeConfig;
            }
        });
    }

    function getComponentInstanceConfig(modulePath) {
        if(_componentInstanceConfigCache[modulePath]) {
            return _componentInstanceConfigCache[modulePath];
        }

        var currentInstanceConfig = applicationConfiguration,
            currentSettings = currentInstanceConfig.settings,
            currentLabels = currentInstanceConfig.labels,
            currentDefaultConfig = {},
            currentDefaultSettings = {},
            currentDefaultLabels = {},
            currentType;

        var paths = modulePath.split('.');
        var currFull = '';

        for (var i = 0; i < paths.length; i++) {
            var pathFragment = paths[i];
            currFull += (currFull ? '.' : '') + pathFragment;

            currentInstanceConfig = currentInstanceConfig.components &&
                currentInstanceConfig.components[pathFragment] ?
                currentInstanceConfig.components[pathFragment] : {};

            currentDefaultConfig = currentDefaultConfig && currentDefaultConfig.components &&
                currentDefaultConfig.components[pathFragment] ?
                currentDefaultConfig.components[pathFragment] : {};

            currentType = currentInstanceConfig.type || currentDefaultConfig.type;

            currentDefaultConfig = defaultConfigCacher(currFull, currentType, currentDefaultConfig);
            currentInstanceConfig = instanceConfigCacher(currFull, currentInstanceConfig, currentDefaultConfig, currentType);

            // cache/get default settings and labels
            currentInstanceConfig.defaultSettings = currentDefaultSettings = defaultSettingsCacher(currFull, currentDefaultSettings, currentDefaultConfig);
            currentInstanceConfig.defaultLabels = currentDefaultLabels = defaultLabelsCacher(currFull, currentDefaultLabels, currentDefaultConfig);

            // cache/get instance settings and labels
            currentInstanceConfig.settings = currentSettings = instanceSettingsCacher(currFull, currentInstanceConfig, currentSettings, currentDefaultSettings);
            currentInstanceConfig.labels = currentLabels = instanceLabelsCacher(currFull, currentInstanceConfig, currentLabels, currentDefaultLabels);

            componentInstanceTypeCache[currFull] = currentType;
            if (!componentInstanceTypeCache[currFull]) {
                throw new Error('Invalid component type for modulePath: ' + currFull);
            }
        }
        return currentInstanceConfig;
    }

    function instanceConfigCacher(currFull, currentInstanceConfig, currentDefaultConfig, currentType) {
        if(_componentInstanceConfigCache[currFull]) {
             return _componentInstanceConfigCache[currFull];
        }

        if(currentDefaultConfig.types) {
            storeTypeConfigs(currentDefaultConfig.types);
        }
        if (currentDefaultConfig.blueprints && currentInstanceConfig.blueprint && currentDefaultConfig.blueprints[currentInstanceConfig.blueprint]) {
            var blueprintConfig = currentDefaultConfig.blueprints[currentInstanceConfig.blueprint];
            currentInstanceConfig = morningstar.asterix.util.merge({}, blueprintConfig, currentInstanceConfig, mergeCustomizer);
        }

        if(currentInstanceConfig.types) {
            storeTypeConfigs(currentInstanceConfig.types);
        }
        if(_typeConfigs[currentType]) {
            currentInstanceConfig = morningstar.asterix.util.merge({}, _typeConfigs[currentType], currentInstanceConfig, mergeCustomizer);
        }

        _componentInstanceConfigCache[currFull] = currentInstanceConfig;
        return _componentInstanceConfigCache[currFull];
    }

    function getComponentInstanceSettings(modulePath) {
        if (componentInstanceSettingsCache[modulePath]) {
            return componentInstanceSettingsCache[modulePath];
        }

        var instanceConfig = getComponentInstanceConfig(modulePath);

        var currentDefaultSettings = util.merge({}, baseSettings, instanceConfig.defaultSettings, instanceConfig.settings, mergeCustomizer);

        // in case we rely on inherited default settings we fallback to currentDefaultConfig.type
        componentInstanceSettingsCache[modulePath] = getModuleSpecificInstanceSettings(currentDefaultSettings,
            componentInstanceTypeCache[modulePath]);
        return componentInstanceSettingsCache[modulePath];
    }

    var autoInherit = ['languageId', 'currencyId', 'currencySymbol', 'defaultNumberFormat', 'defaultDateFormat',
        'format',
        'validation', '_validation'
    ];

    function shouldAutoInherit(key) {
        return autoInherit.indexOf(key) > -1;
    }

    function getModuleSpecificInstanceSettings(settings, moduleType) {
        var defaultSettings = defaultConfiguration[moduleType] &&
            defaultConfiguration[moduleType].settings ? defaultConfiguration[moduleType].settings : {};

        var keys = Object.keys(settings);
        for (var i = 0; i < keys.length; i++) {
            if (defaultSettings[keys[i]] === undefined &&
                (!defaultSettings.__allowed || defaultSettings.__allowed.indexOf(keys[i]) < 0) &&
                !shouldAutoInherit(keys[i])) {
                delete settings[keys[i]];
            }
        }

        return settings;
    }

    function getComponentInstanceLabels(modulePath, languageId) {
        var moduleSpecificInstanceLabels = componentInstanceLabelsCache[modulePath];
        if (!moduleSpecificInstanceLabels) {

            var instanceConfig = getComponentInstanceConfig(modulePath);
            var currentDefaultLabels = util.merge({}, instanceConfig.defaultLabels, instanceConfig.labels, mergeCustomizer);

            moduleSpecificInstanceLabels = currentDefaultLabels;
            componentInstanceLabelsCache[modulePath] = moduleSpecificInstanceLabels;
        }

        var languageLabels = componentInstanceLanguageLabelsCache[modulePath] &&
            componentInstanceLanguageLabelsCache[modulePath][languageId] ?
            componentInstanceLanguageLabelsCache[modulePath][languageId] : null;
        if (!languageLabels) {
            languageLabels = {};

            // todo: fix implementation to support x1-x2-x3-x4-x5-..-xn
            var baseLanguage = null;
            if (languageId.split('-').length > 1) {
                baseLanguage = languageId.split('-')[0];
            }

            var finalLabels = extend(true, {}, baseLabels, moduleSpecificInstanceLabels);

            util.each(finalLabels, function(moduleSpecificInstanceLabelValue, moduleSpecificInstanceLabelKey) {
                if (!moduleSpecificInstanceLabelValue) {
                    return;
                }

                if (moduleSpecificInstanceLabelValue[languageId]) {
                    languageLabels[moduleSpecificInstanceLabelKey] = moduleSpecificInstanceLabelValue[languageId];
                } else if (baseLanguage && moduleSpecificInstanceLabelValue[baseLanguage]) {
                    languageLabels[moduleSpecificInstanceLabelKey] = moduleSpecificInstanceLabelValue[baseLanguage];
                } else if (moduleSpecificInstanceLabelValue.en) {
                    // default to en if no label found for languageId
                    languageLabels[moduleSpecificInstanceLabelKey] = moduleSpecificInstanceLabelValue.en;
                }
            });

            if (!componentInstanceLanguageLabelsCache[modulePath]) {
                componentInstanceLanguageLabelsCache[modulePath] = {};
            }

            componentInstanceLanguageLabelsCache[modulePath][languageId] = languageLabels;
        }

        return languageLabels;
    }

    function getComponentInstanceType(modulePath) {
        if (componentInstanceTypeCache[modulePath]) {
            return componentInstanceTypeCache[modulePath];
        }

        var currentInstance = applicationConfiguration,
            currentDefaultConfig = {},
            currType;

        var paths = modulePath.split('.');
        for (var i = 0; i < paths.length; i++) {
            var componentInstanceId = paths[i];
            currentInstance = currentInstance.components &&
                currentInstance.components[componentInstanceId] ?
                currentInstance.components[componentInstanceId] : {};
            currentDefaultConfig = currentDefaultConfig && currentDefaultConfig.components &&
                currentDefaultConfig.components[componentInstanceId] ?
                currentDefaultConfig.components[componentInstanceId] : (!currentInstance.type ? {} : defaultConfiguration[currentInstance.type]);
            currType = currentInstance.type || currentDefaultConfig.type;
            currentDefaultConfig.components = extend({}, currentDefaultConfig.components, !currType ? {} : defaultConfiguration[currType].components);
        }

        componentInstanceTypeCache[modulePath] = currType;

        return componentInstanceTypeCache[modulePath];
    }

    function getSetting(modulePath, settingKey) {
        return getComponentInstanceSettings(modulePath)[settingKey];
    }

    function init(applicationConfig, defaultConfig, baseLabelsIn, baseSettingsIn, optionsIn) {
        if (!applicationConfig) {
            throw new Error('(config) No applicationConfig provided');
        }

        if (!defaultConfig) {
            throw new Error('(config) No defaultConfig provided');
        }

        _componentInstanceConfigCache = {};
        componentInstanceSettingsCache = {};
        componentInstanceLabelsCache = {};
        componentInstanceLanguageLabelsCache = {};
        componentInstanceTypeCache = {};
        modulePaths = {};
        _defaultConfigFragmentCache = {};
        _defaultSettingsFragmentCache = {};
        _instanceSettingsFragmentCache = {};
        _defaultLabelsFragmentCache = {};
        _instanceLabelsFragmentCache = {};
        applicationConfiguration = applicationConfig;
        defaultConfiguration = defaultConfig;
        baseLabels = extend(true, {}, defaultBaseLabels, baseLabelsIn);
        baseSettings = extend({}, defaultBaseSettings, baseSettingsIn);
        options = optionsIn || {};
        if (!options.hasOwnProperty('dynamic')) {
            options.dynamic = false;
        }

        util.each(Object.keys(baseSettings), function(key) {
            if (autoInherit.indexOf(key) < 0) {
                autoInherit.push(key);
            }
        });

        _applyEnv(applicationConfiguration);
        _applyEnv(defaultConfiguration);

        registerModulePaths(applicationConfig.components);
    }

    function registerModulePaths(components, pathPrefix) {
        if (Object.keys(components).length === 0) {
            return;
        }

        util.each(components, function(component, pathFragment) {
            var fullPath = (pathPrefix ? pathPrefix + '.' : '') + pathFragment;
            if (!modulePaths.hasOwnProperty(fullPath)) {
                modulePaths[fullPath] = true;
            }

            if (!component.type) {
                throw new Error('Missing component type for path: ' + fullPath);
            }

            registerModulePaths(extend({}, defaultConfiguration.hasOwnProperty(component.type) ?
                defaultConfiguration[component.type].components : {}, component.components), fullPath);
        });
    }

    function getModulePaths() {
        return modulePaths;
    }

    function getComponentType(modulePath) {
        return getComponentInstanceType(modulePath);
    }

    function hasType(type) {
        return !!defaultConfiguration[type];
    }

    function hasInstance(modulePath) {
        return !!modulePaths[modulePath];
    }

    function _addInstance(modulePath, opts) {
        if (!modulePath || !opts || !opts.type) {
            throw new Error('(config) _addInstance requires modulePath and opts (with type)');
        }

        util.componentPath.assignInstance(modulePath, applicationConfiguration, defaultConfiguration, opts.type, opts.instanceConfig, function(modulePath) {
            modulePaths[modulePath] = true;

            // invalidate fragment cache for ancestors
            util.each([_defaultConfigFragmentCache, _defaultLabelsFragmentCache, _defaultSettingsFragmentCache, _instanceLabelsFragmentCache, _instanceSettingsFragmentCache, _componentInstanceConfigCache], function(cache, key) {
                util.each(cache, function(entry, key) {
                    if (modulePath.indexOf(key) === 0) {
                        delete cache[key];
                    }
                });
            });
        });
    }

    function allowsDynamic() {
        return !options || options.dynamic;
    }

    function addType(type, defaultConfig) {
        if (!allowsDynamic()) {
            throw new Error('(config) The configuration module has been configured not to allow dynamic behaviour.');
        }

        defaultConfig = defaultConfig || namespace.getDefaultConfig(type);

        if (!type || !defaultConfig) {
            throw new Error('(config) _addType requires type and defaultConfig');
        }

        if (!defaultConfig.type) {
            defaultConfig.type = type;
        }

        defaultConfiguration[type] = defaultConfig;
    }

    function addInstance(modulePath, opts) {
        if (!canAddModulePathDynamically(modulePath)) {
            throw new Error('(config) The configuration module has been configured not to allow dynamic behaviour.');
        }

        if (!modulePath || !opts) {
            throw new Error('(config) addInstance requires modulePath and options');
        }

        if (!opts.type) {
            throw new Error('(config) addInstance requires options.type');
        }

        if (!hasType(opts.type)) {
            addType(opts.type, opts.defaultConfig);
        }

        if (!hasInstance(modulePath)) {
            _addInstance(modulePath, opts);
        }
    }

    function canAddModulePathDynamically(modulePath) {
        return allowsDynamic() || (modulePath && modulePath.indexOf('[') >= 0 &&
            hasInstance(modulePath.substring(0, modulePath.lastIndexOf('['))));
    }

    return {
        init: init,
        getSetting: getSetting,
        getSettings: getComponentInstanceSettings,
        getLabels: getComponentInstanceLabels,
        getComponentType: getComponentType,
        getModulePaths: getModulePaths,
        addType: addType,
        addInstance: addInstance,
        hasType: hasType,
        hasInstance: hasInstance,
        canAddModulePathDynamically: canAddModulePathDynamically,
        allowsDynamic: allowsDynamic,
        /**
         * Note: instanceId can only be a top level instance.
         * @param instanceId
         * @returns {*}
         */
        getInstanceConfig: function getDefaultConfig(instanceId) {
            return !applicationConfiguration || !applicationConfiguration.components ||
                !applicationConfiguration.components[instanceId] ? null :
                applicationConfiguration.components[instanceId];
        },
        getDefaultConfig: function getDefaultConfig(type) {
            return !defaultConfiguration || !defaultConfiguration[type] ? null : defaultConfiguration[type];
        },
        getOption: function getOption(key) {
            return (options || {})[key];
        },
        setOption: function setOption(key, value) {
            options = options || {};
            options[key] = value;
        },
        getBaseSettings: function getBaseSettings() {
            return defaultBaseSettings;
        },
        mergeBaseSettings: function mergeBaseSettings(settings) {
            defaultBaseSettings = util.merge({}, defaultBaseSettings, settings);
        }
    };
});



/*****************
 ** WEBPACK FOOTER
 ** ./components/core/src/js/config.js
 ** module id = 112
 ** module chunks = 0
 **/